import {
  type ApartmentContact,
  type ApartmentNote,
  type ApartmentWorkspace as Workspace,
  type HandoverType,
  type UnitHistoryResponse,
  apartmentNoteSchema,
  apartmentWorkspaceResponseSchema,
  handoverDocumentSchema,
  unitHistoryResponseSchema,
} from '@sivan/shared';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { ApiError, NetworkError, apiGet, apiPost, apiPut } from '../api/client';
import { chooseProject } from '../current-project';
import { formatLocation } from '../handover/location';
import { useLoad } from '../master-data/use-master-data';
import { reportPendingChanges } from '../master-data/unsaved-changes';
import { navigate, useHash } from '../routing';
import { AppLink } from '../ui/AppLink';
import { Breadcrumbs } from '../ui/Breadcrumbs';
import { EmptyState } from '../ui/EmptyState';
import { Icon } from '../ui/Icon';
import { formatNavigatorLocation } from '../structure/navigator-model';
import { parseApartmentLocation } from './location';
import { StatusBadge } from './StatusBadge';
import { EXCEPTION_META, STAGE_META, defectMeta } from './status';
import {
  DEFECT_STATE,
  type NextAction,
  type Stage,
  TYPE_LABELS,
  defectCounts,
  latestOf,
  nextAction,
  stageOf,
} from './workspace-model';

const RELATIONSHIPS: Readonly<Record<ApartmentContact['relationship'], string>> = {
  source_listed: 'רשום במקור',
  purchaser: 'רוכש',
  additional_purchaser: 'רוכש נוסף',
  occupant: 'דייר',
  contact_person: 'איש קשר',
};

const dateTime = new Intl.DateTimeFormat('he-IL', {
  timeZone: 'Asia/Jerusalem',
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});
const day = new Intl.DateTimeFormat('he-IL', {
  timeZone: 'Asia/Jerusalem',
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
});

const handoverHref = (w: Workspace, handoverId: string | null) =>
  formatLocation({ projectId: w.project.id, apartmentId: w.apartment.id, handoverId });

/**
 * The apartment workspace (U2, docs/product-ux 05 · C): one page for one apartment. Who it is,
 * where its handovers stand, the one next step, open defects, parking and storage, residents
 * (only with the contact keys: the server sends nothing otherwise), the note and the history.
 * Performing a handover stays in the handover screen; bulk editing stays in Master Data.
 */
export function ApartmentWorkspace() {
  const location = parseApartmentLocation(useHash());
  const key = location === null ? 'none' : `${location.projectId}/${location.apartmentId}`;
  const [loaded, reload] = useLoad(`apartment:${key}`, () =>
    location === null
      ? Promise.reject(new ApiError(404, {}))
      : apiGet(
          `/projects/${location.projectId}/apartments/${location.apartmentId}`,
          apartmentWorkspaceResponseSchema,
        ),
  );
  // The apartment's project becomes the current one, so every other screen continues there.
  const projectId = loaded.state === 'ready' ? loaded.data.project.id : null;
  useEffect(() => {
    if (projectId !== null) chooseProject(projectId);
  }, [projectId]);

  if (loaded.state === 'loading')
    return (
      <p className="ws-loading" role="status">
        טוען את הדירה…
      </p>
    );
  if (loaded.state === 'forbidden')
    return (
      <>
        <h1>דירה</h1>
        <EmptyState icon="lock" title="הדירה אינה זמינה">
          <p>ייתכן שהקישור שגוי, או שאין לך גישה לפרויקט שלה. לא נחשף מידע נוסף.</p>
        </EmptyState>
      </>
    );
  if (loaded.state === 'error')
    return (
      <>
        <h1>דירה</h1>
        <EmptyState
          icon="alert"
          title="טעינת הדירה נכשלה"
          action={
            <button type="button" className="button button--secondary" onClick={reload}>
              ניסיון חוזר
            </button>
          }
        >
          {loaded.requestId !== null && <p>מזהה בקשה: {loaded.requestId}</p>}
        </EmptyState>
      </>
    );
  return <WorkspaceView key={loaded.data.apartment.id} workspace={loaded.data} />;
}

function WorkspaceView({ workspace: w }: { readonly workspace: Workspace }) {
  const a = w.apartment;
  const floor = a.floor === '' ? 'ללא קומה' : a.floor;
  const counts = defectCounts(w);
  const stages =
    w.handovers === null
      ? null
      : (['initial', 'final'] as const).map((t) => stageOf(latestOf(w.handovers ?? [], t), t));
  const missingType = a.modelType === null;
  const defects =
    w.defects === null ? null : defectMeta({ open: counts.open, awaiting: counts.awaiting });
  return (
    <div className="ws">
      <Breadcrumbs
        items={[
          { label: w.project.name, to: '#home' },
          {
            label: 'מבנה ודירות',
            to: formatNavigatorLocation({
              projectId: w.project.id,
              buildingId: a.buildingId,
              apartmentId: a.id,
            }),
          },
          { label: `בניין ${a.building} · ${floor}` },
        ]}
      />
      <section className="ws-head" aria-labelledby="ws-title">
        <div className="ws-head__main">
          <p className="ws-eyebrow">{w.project.name}</p>
          <h1 id="ws-title" className="ws-title">
            בניין <bdi>{a.building}</bdi> · דירה <bdi className="num">{a.apartmentNumber}</bdi>
          </h1>
          <dl className="ws-facts">
            <Fact label="קומה">
              {floor}
              {a.sourceFloorLabel !== null && (
                <span className="ws-muted"> (במקור: {a.sourceFloorLabel})</span>
              )}
            </Fact>
            <Fact label="מס׳ מקורי">
              {a.sourceNumber === null ? '—' : <bdi className="num">{a.sourceNumber}</bdi>}
            </Fact>
            <Fact label="טיפוס דירה">
              {missingType ? <span className="chip chip--alert">חסר</span> : a.modelType}
            </Fact>
          </dl>
          {/* The three dimensions, apart (08 §1), as the server computed them. */}
          <ul className="ws-dims" aria-label="מצב הדירה">
            <li>
              <StatusBadge meta={STAGE_META[w.stage]} />
            </li>
            {defects !== null && (
              <li>
                <StatusBadge meta={defects} />
              </li>
            )}
            {w.exceptions.map((e) => (
              <li key={e}>
                <StatusBadge meta={EXCEPTION_META[e]} />
              </li>
            ))}
          </ul>
        </div>
        <NextStep workspace={w} />
      </section>

      <div className="ws-cols">
        <div className="ws-stack">
          <Card id="ws-handovers" icon="clipboard" title="מסירות">
            {stages === null ? (
              <Restricted>מסירות הדירה זמינות רק בהרשאת מסירות.</Restricted>
            ) : (
              <ol className="ws-stages">
                {stages.map((s) => (
                  <StageRow key={s.type} stage={s} workspace={w} />
                ))}
              </ol>
            )}
          </Card>
          <Card
            id="ws-defects"
            icon="alert"
            title="ליקויים פתוחים"
            meta={
              w.defects === null
                ? undefined
                : `${String(counts.open)} פתוחים${counts.awaiting > 0 ? ` · ${String(counts.awaiting)} לבדיקה חוזרת` : ''}`
            }
          >
            {w.defects === null ? (
              <Restricted>הליקויים זמינים רק בהרשאת מסירות.</Restricted>
            ) : w.defects.length === 0 ? (
              <p className="ws-muted">אין ליקויים פתוחים לדירה זו.</p>
            ) : (
              <ul className="ws-defects">
                {w.defects.map((d) => {
                  const state = DEFECT_STATE[d.reinspection ?? 'open'];
                  return (
                    <li key={d.id} className="ws-defect">
                      <span className="ws-defect__where">
                        {d.area} · {d.component}
                      </span>
                      <span className="ws-defect__what">{d.description}</span>
                      <span className="ws-defect__meta">
                        {d.contractor ?? 'ללא קבלן'} · {TYPE_LABELS[d.handoverType]}{' '}
                        <AppLink to={handoverHref(w, d.handoverId)}>
                          <bdi className="num">{d.protocolNumber}</bdi>
                        </AppLink>
                      </span>
                      <span className={`state state--${state.tone}`}>{state.label}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>
        <div className="ws-stack">
          <Card
            id="ws-contacts"
            icon="users"
            title="דיירים"
            meta={w.contacts === null ? undefined : 'מידע מוגבל'}
          >
            {w.contacts === null ? (
              <Restricted>
                פרטי הדיירים זמינים רק בהרשאת &quot;פרטי דיירים&quot;. הם אינם נשלחים לדפדפן בלי
                ההרשאה.
              </Restricted>
            ) : w.contacts.length === 0 ? (
              <p className="ws-muted">לא רשומים דיירים לדירה זו.</p>
            ) : (
              <ul className="ws-contacts">
                {w.contacts.map((c, index) => (
                  <li key={index} className="ws-contact">
                    <span className="ws-contact__name">{c.name ?? 'ללא שם'}</span>
                    <span className="ws-muted">{RELATIONSHIPS[c.relationship]}</span>
                    <span className="ws-contact__links">
                      {c.phones.map((p) => (
                        <a key={p} href={`tel:${p}`}>
                          <bdi className="num">{p}</bdi>
                        </a>
                      ))}
                      {c.emails.map((e) => (
                        <a key={e} href={`mailto:${e}`}>
                          <bdi>{e}</bdi>
                        </a>
                      ))}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card id="ws-spaces" icon="building" title="חניה ומחסן">
            {a.parking.length === 0 && a.storage.length === 0 ? (
              <p className="ws-muted">לא הוצמדו חניה או מחסן.</p>
            ) : (
              <ul className="ws-spaces">
                {(['parking', 'storage'] as const).flatMap((kind) =>
                  a[kind].map((s, index) => (
                    <li key={`${kind}-${String(index)}`} className="ws-space">
                      <strong>
                        {kind === 'parking' ? 'חניה' : 'מחסן'} <bdi className="num">{s.number}</bdi>
                      </strong>
                      <span className="ws-muted">
                        {s.location === '' ? s.structureCode : `מפלס ${s.location}`}
                      </span>
                    </li>
                  )),
                )}
              </ul>
            )}
            {w.permissions.masterData && (
              <p className="ws-card-foot">
                <AppLink to="#master-data">עריכה בנתוני האב</AppLink>
              </p>
            )}
          </Card>
          <NoteCard workspace={w} />
          {w.permissions.history && <History workspace={w} />}
        </div>
      </div>
    </div>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="ws-fact">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Card({
  id,
  icon,
  title,
  meta,
  children,
}: {
  readonly id: string;
  readonly icon: 'clipboard' | 'alert' | 'users' | 'building' | 'file' | 'clock';
  readonly title: string;
  readonly meta?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section className="ws-card" aria-labelledby={id}>
      <header className="ws-card__head">
        <h2 id={id}>
          <Icon name={icon} />
          {title}
        </h2>
        {meta !== undefined && <span className="ws-muted">{meta}</span>}
      </header>
      <div className="ws-card__body">{children}</div>
    </section>
  );
}

function Restricted({ children }: { readonly children: ReactNode }) {
  return (
    <p className="ws-restricted">
      <Icon name="lock" />
      <span>{children}</span>
    </p>
  );
}

function StageRow({ stage, workspace }: { readonly stage: Stage; readonly workspace: Workspace }) {
  const appointment = workspace.appointments?.[stage.type] ?? null;
  return (
    <li className={`ws-stage ws-stage--${stage.type}`}>
      <div className="ws-stage__head">
        <span className="ws-stage__name">{TYPE_LABELS[stage.type]}</span>
        {stage.handover !== null && (
          <AppLink to={handoverHref(workspace, stage.handover.id)} className="ws-stage__link">
            <bdi className="num">{stage.handover.protocolNumber}</bdi>
          </AppLink>
        )}
      </div>
      <div
        className="ws-progress"
        role="progressbar"
        aria-label={`התקדמות ה${TYPE_LABELS[stage.type]}`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={stage.percent}
      >
        <i style={{ inlineSize: `${String(stage.percent)}%` }} />
      </div>
      <p className="ws-stage__text">
        {stage.text}
        {stage.handover?.handoverDate != null &&
          ` · ${day.format(new Date(`${stage.handover.handoverDate}T12:00:00Z`))}`}
      </p>
      {appointment !== null && (
        <p className="ws-muted">
          <Icon name="calendar" /> פגישה: {dateTime.format(new Date(appointment.startsAt))}
          {appointment.primary !== null && ` · ${appointment.primary}`}
        </p>
      )}
    </li>
  );
}

/** The one primary action of the page: continue or open a handover in one step. */
function NextStep({ workspace }: { readonly workspace: Workspace }) {
  const next: NextAction = nextAction(workspace);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const open = (handoverId: string) => {
    navigate(handoverHref(workspace, handoverId), 'push');
  };
  const create = async (type: HandoverType) => {
    setBusy(true);
    setMessage(null);
    try {
      const doc = await apiPost(
        `/projects/${workspace.project.id}/handovers`,
        { apartmentId: workspace.apartment.id, type },
        handoverDocumentSchema,
      );
      open(doc.id);
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status === 409 &&
        typeof error.body['existingId'] === 'string'
      )
        // Someone opened the same draft meanwhile: continue with that one.
        open(error.body['existingId']);
      else if (error instanceof ApiError && error.status === 403)
        setMessage('אין לך הרשאה לפתוח מסירה בפרויקט זה.');
      else if (error instanceof NetworkError)
        setMessage('אין חיבור לשרת. לא נפתחה מסירה; נסו שוב.');
      else setMessage('פתיחת המסירה נכשלה. נסו שוב.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <aside className="ws-next" aria-labelledby="ws-next-title">
      <p className="ws-next__label">הפעולה הבאה</p>
      <p className="ws-next__title" id="ws-next-title">
        {next.title}
      </p>
      {next.kind === 'open' && (
        <AppLink
          className="button button--primary ws-next__action"
          to={handoverHref(workspace, next.handoverId)}
        >
          {next.button}
        </AppLink>
      )}
      {next.kind === 'create' && (
        <button
          type="button"
          className="button button--primary ws-next__action"
          disabled={busy}
          onClick={() => {
            void create(next.type);
          }}
        >
          {busy ? 'פותח…' : next.button}
        </button>
      )}
      {next.kind === 'none' && next.reason !== '' && (
        <p className="ws-next__reason">{next.reason}</p>
      )}
      {message !== null && (
        <p className="ws-next__reason" role="alert">
          {message}
        </p>
      )}
      {workspace.permissions.handoverView && (
        <p className="ws-next__more">
          <AppLink to="#schedule">
            <Icon name="calendar" />
            יומן מסירות
          </AppLink>
        </p>
      )}
    </aside>
  );
}

const NOTE_MAX = 2000;

/**
 * The apartment's operational note: its own keys (DR-187), saved explicitly from the version it
 * was read at. A conflict keeps the user's text in the field and loads the newer version, so the
 * next save is a deliberate choice.
 */
function NoteCard({ workspace }: { readonly workspace: Workspace }) {
  const fieldId = useId();
  const [note, setNote] = useState<ApartmentNote | null>(workspace.note);
  const [text, setText] = useState(workspace.note?.text ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved' | 'conflict' | 'failed'>('idle');
  const saved = note?.text ?? '';
  const dirty = text.trim() !== saved;
  useEffect(() => {
    reportPendingChanges(dirty ? 1 : 0, dirty ? 'הערת הדירה לא נשמרה. לעזוב ולבטל אותה?' : null);
  }, [dirty]);
  useEffect(
    () => () => {
      reportPendingChanges(0);
    },
    [],
  );
  if (note === null)
    return (
      <Card id="ws-note" icon="file" title="הערות הדירה">
        <Restricted>הערות הדירה אינן זמינות בתפקיד שלך.</Restricted>
      </Card>
    );
  const path = `/projects/${workspace.project.id}/apartments/${workspace.apartment.id}`;
  const save = async () => {
    setState('saving');
    try {
      const stored = await apiPut(
        `${path}/note`,
        { text: text.trim() === '' ? null : text, rowVersion: note.rowVersion },
        apartmentNoteSchema,
      );
      setNote(stored);
      setText(stored.text ?? '');
      setState('saved');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setState('conflict');
        try {
          setNote((await apiGet(path, apartmentWorkspaceResponseSchema)).note);
        } catch {
          setState('failed');
        }
      } else setState('failed');
    }
  };
  return (
    <Card id="ws-note" icon="file" title="הערות הדירה" meta="תפעולי · נפרד מפרטי הדיירים">
      {workspace.permissions.noteEdit ? (
        <form
          className="ws-note"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <label htmlFor={fieldId} className="visually-hidden">
            הערות הדירה
          </label>
          <textarea
            id={fieldId}
            value={text}
            maxLength={NOTE_MAX}
            rows={4}
            placeholder="הערה תפעולית לדירה. לא לרשום כאן פרטים אישיים של דיירים."
            onChange={(e) => {
              setText(e.target.value);
              if (state !== 'conflict') setState('idle');
            }}
          />
          {state === 'conflict' && (
            <div className="notice notice--warning" role="alert">
              <p>ההערה שונתה בינתיים. הטקסט שלכם נשאר בשדה; הגרסה השמורה כעת:</p>
              <p className="ws-note__text">{saved === '' ? '(ריקה)' : saved}</p>
              <button
                type="button"
                className="button button--secondary"
                onClick={() => {
                  setText(saved);
                  setState('idle');
                }}
              >
                להשתמש בגרסה השמורה
              </button>
            </div>
          )}
          <div className="ws-note__actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={!dirty || state === 'saving'}
            >
              {state === 'saving' ? 'שומר…' : 'שמירה'}
            </button>
            <button
              type="button"
              className="button button--ghost"
              disabled={!dirty || state === 'saving'}
              onClick={() => {
                setText(saved);
                setState('idle');
              }}
            >
              ביטול
            </button>
            <span className="ws-muted" role="status">
              {state === 'saved'
                ? 'נשמר'
                : state === 'failed'
                  ? 'השמירה נכשלה. הטקסט נשאר כאן; נסו שוב.'
                  : `${String(text.length)}/${String(NOTE_MAX)}`}
            </span>
          </div>
        </form>
      ) : saved === '' ? (
        <p className="ws-muted">אין הערות.</p>
      ) : (
        <p className="ws-note__text">{saved}</p>
      )}
    </Card>
  );
}

const describe = (item: UnitHistoryResponse['items'][number], contactView: boolean): string => {
  if (item.action === 'projects.unit.created') return 'הדירה נוצרה';
  if (item.action === 'projects.unit.updated') return 'פרטי הדירה עודכנו';
  const fields = String(item.after?.['fields'] ?? '').split(',');
  const note = fields.includes('remarks');
  const contacts = fields.some((f) => f !== 'remarks' && f !== '');
  if (contacts && !contactView) return note ? 'עודכנו הערה ופרטים מוגבלים' : 'עודכנו פרטים מוגבלים';
  if (contacts) return note ? 'עודכנו פרטי הדיירים וההערה' : 'עודכנו פרטי הדיירים';
  return 'הערת הדירה עודכנה';
};

/** The apartment's change history, read only when opened (`audit.project.view`). */
function History({ workspace }: { readonly workspace: Workspace }) {
  const [opened, setOpened] = useState(false);
  const [items, setItems] = useState<UnitHistoryResponse['items'] | 'failed' | null>(null);
  useEffect(() => {
    if (!opened) return;
    let current = true;
    apiGet(
      `/projects/${workspace.project.id}/units/${workspace.apartment.id}/history`,
      unitHistoryResponseSchema,
    ).then(
      (r) => {
        if (current) setItems(r.items);
      },
      () => {
        if (current) setItems('failed');
      },
    );
    return () => {
      current = false;
    };
  }, [opened, workspace.project.id, workspace.apartment.id]);
  return (
    <details
      className="ws-card ws-history"
      onToggle={(e) => {
        if (e.currentTarget.open) setOpened(true);
      }}
    >
      <summary>
        <Icon name="clock" />
        היסטוריה
      </summary>
      {items === null ? (
        <p className="ws-muted" role="status">
          טוען…
        </p>
      ) : items === 'failed' ? (
        <p className="ws-muted">טעינת ההיסטוריה נכשלה.</p>
      ) : items.length === 0 ? (
        <p className="ws-muted">אין אירועים.</p>
      ) : (
        <ol className="ws-history__list">
          {items.map((item) => (
            <li key={item.id}>
              <strong>{dateTime.format(new Date(item.occurredAt))}</strong> ·{' '}
              {describe(item, workspace.permissions.contactView)}
              {item.actor !== null && <span className="ws-muted"> · {item.actor.displayName}</span>}
            </li>
          ))}
        </ol>
      )}
    </details>
  );
}
